import React, { useState, useEffect } from 'react';
import { 
  Plus, Calendar, Clock, AlertTriangle, CheckCircle, 
  Package, Pause, Play, X, Edit, Trash2, QrCode,
  FileText, Download, Eye, Filter, Search
} from 'lucide-react';
import { supabase } from '@/config/supabase';
import { format } from 'date-fns';
import { pl } from 'date-fns/locale';
import { QRCodeSVG } from 'qrcode.react';
import { NewOrderModal } from './NewOrderModal';

interface ProductionOrder {
  id: string;
  order_number: string;
  client_name: string;
  products: any[];
  priority: number;
  status: 'planned' | 'in_production' | 'completed' | 'cancelled';
  created_at: string;
  scheduled_date: string;
  deadline: string;
  started_at?: string;
  completed_at?: string;
  assigned_to?: string;
  production_time?: number;
  material_usage?: number;
  notes?: string;
  qr_code?: string;
}

export const ProductionQueue: React.FC = () => {
  const [orders, setOrders] = useState<ProductionOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'planned' | 'in_production' | 'completed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ProductionOrder | null>(null);
  const [showQRModal, setShowQRModal] = useState(false);

  // Pobierz zlecenia z bazy
  useEffect(() => {
    fetchOrders();
  }, [filter]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('production_orders')
        .select('*')
        .order('priority', { ascending: false })
        .order('deadline', { ascending: true });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setOrders(data || []);
    } catch (error) {
      console.error('Błąd pobierania zleceń:', error);
    } finally {
      setLoading(false);
    }
  };

  // Zmień status zlecenia
  const updateOrderStatus = async (orderId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('production_orders')
        .update({ status: newStatus })
        .eq('id', orderId);

      if (error) throw error;
      
      // Odśwież listę
      fetchOrders();
      
      // Pokaż powiadomienie
      alert(`Status zmieniony na: ${getStatusLabel(newStatus)}`);
    } catch (error) {
      console.error('Błąd aktualizacji statusu:', error);
      alert('Wystąpił błąd podczas aktualizacji statusu');
    }
  };

  // Usuń zlecenie
  const deleteOrder = async (orderId: string) => {
    if (!confirm('Czy na pewno chcesz usunąć to zlecenie?')) return;

    try {
      const { error } = await supabase
        .from('production_orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;
      fetchOrders();
      alert('Zlecenie zostało usunięte');
    } catch (error) {
      console.error('Błąd usuwania zlecenia:', error);
      alert('Wystąpił błąd podczas usuwania zlecenia');
    }
  };

  // Generuj kod QR
  const generateQRCode = (order: ProductionOrder) => {
    const qrData = {
      id: order.id,
      number: order.order_number,
      client: order.client_name,
      status: order.status,
      url: `${window.location.origin}/production/order/${order.id}`
    };
    return JSON.stringify(qrData);
  };

  // Pomocnicze funkcje
  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      planned: 'Zaplanowane',
      in_production: 'W produkcji',
      completed: 'Zrealizowane',
      cancelled: 'Anulowane'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      planned: 'bg-gray-500/20 text-gray-400',
      in_production: 'bg-yellow-500/20 text-yellow-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getPriorityIcon = (priority: number) => {
    if (priority >= 2) return <AlertTriangle className="w-4 h-4 text-red-500" />;
    if (priority === 1) return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
    return null;
  };

  const calculateTimeRemaining = (deadline: string) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days} dni`;
    if (hours > 0) return `${hours} godz.`;
    return 'Termin minął';
  };

  // Filtrowane zlecenia
  const filteredOrders = orders.filter(order => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        order.order_number.toLowerCase().includes(search) ||
        order.client_name.toLowerCase().includes(search) ||
        order.notes?.toLowerCase().includes(search)
      );
    }
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Nagłówek z filtrami */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
        <div className="flex items-center gap-4 w-full lg:w-auto">
          {/* Wyszukiwarka */}
          <div className="relative flex-1 lg:flex-initial">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Szukaj zlecenia..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white w-full lg:w-64 focus:border-orange-500 focus:outline-none"
            />
          </div>

          {/* Filtry statusu */}
          <div className="flex gap-2">
            {(['all', 'planned', 'in_production', 'completed'] as const).map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-3 py-1 rounded-lg text-sm transition-all ${
                  filter === status 
                    ? 'bg-orange-500 text-white' 
                    : 'bg-zinc-700 text-gray-400 hover:bg-zinc-600'
                }`}
              >
                {status === 'all' ? 'Wszystkie' : getStatusLabel(status)}
              </button>
            ))}
          </div>
        </div>

        {/* Przycisk nowego zlecenia */}
        <button
          onClick={() => setShowNewOrderModal(true)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nowe zlecenie
        </button>
      </div>

      {/* Lista zleceń */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-zinc-700 rounded-lg p-12 text-center">
          <Package className="w-16 h-16 text-gray-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Brak zleceń</h3>
          <p className="text-gray-400">
            {searchTerm ? 'Nie znaleziono zleceń spełniających kryteria' : 'Dodaj pierwsze zlecenie produkcyjne'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredOrders.map(order => (
            <div key={order.id} className="bg-zinc-700 rounded-lg p-4 hover:bg-zinc-600 transition-all">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-white text-lg">
                      Zlecenie #{order.order_number}
                    </h3>
                    {getPriorityIcon(order.priority)}
                    <span className={`text-sm px-3 py-1 rounded-full ${getStatusColor(order.status)}`}>
                      {getStatusLabel(order.status)}
                    </span>
                  </div>
                  
                  <p className="text-gray-300 mb-3">
                    <span className="font-medium">Klient:</span> {order.client_name}
                  </p>
                  
                  {order.products && order.products.length > 0 && (
                    <p className="text-sm text-gray-400 mb-3">
                      <span className="font-medium">Produkty:</span> {order.products.map((p: any) => p.name).join(', ')}
                    </p>
                  )}

                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="text-gray-400 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Termin: {format(new Date(order.deadline), 'dd.MM.yyyy', { locale: pl })}
                    </span>
                    
                    {order.status !== 'completed' && (
                      <span className="text-gray-400 flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {calculateTimeRemaining(order.deadline)}
                      </span>
                    )}
                    
                    {order.material_usage && (
                      <span className="text-green-400 flex items-center gap-1">
                        <Package className="w-4 h-4" />
                        Wykorzystanie: {order.material_usage}%
                      </span>
                    )}
                  </div>

                  {order.notes && (
                    <p className="text-sm text-gray-500 mt-2 italic">
                      {order.notes}
                    </p>
                  )}
                </div>

                {/* Przyciski akcji */}
                <div className="flex items-center gap-2 ml-4">
                  {/* Zmiana statusu */}
                  {order.status === 'planned' && (
                    <button
                      onClick={() => updateOrderStatus(order.id, 'in_production')}
                      className="p-2 bg-yellow-500/20 text-yellow-400 rounded hover:bg-yellow-500/30 transition-all"
                      title="Rozpocznij produkcję"
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  )}
                  
                  {order.status === 'in_production' && (
                    <>
                      <button
                        onClick={() => updateOrderStatus(order.id, 'completed')}
                        className="p-2 bg-green-500/20 text-green-400 rounded hover:bg-green-500/30 transition-all"
                        title="Zakończ produkcję"
                      >
                        <CheckCircle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => updateOrderStatus(order.id, 'planned')}
                        className="p-2 bg-gray-500/20 text-gray-400 rounded hover:bg-gray-500/30 transition-all"
                        title="Wstrzymaj produkcję"
                      >
                        <Pause className="w-4 h-4" />
                      </button>
                    </>
                  )}

                  {/* Kod QR */}
                  <button
                    onClick={() => {
                      setSelectedOrder(order);
                      setShowQRModal(true);
                    }}
                    className="p-2 bg-blue-500/20 text-blue-400 rounded hover:bg-blue-500/30 transition-all"
                    title="Pokaż kod QR"
                  >
                    <QrCode className="w-4 h-4" />
                  </button>

                  {/* Szczegóły */}
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="p-2 bg-zinc-600 text-gray-400 rounded hover:bg-zinc-500 transition-all"
                    title="Szczegóły"
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {/* Usuń */}
                  {order.status !== 'in_production' && (
                    <button
                      onClick={() => deleteOrder(order.id)}
                      className="p-2 bg-red-500/20 text-red-400 rounded hover:bg-red-500/30 transition-all"
                      title="Usuń zlecenie"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal z kodem QR */}
      {showQRModal && selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-800 rounded-lg p-6 max-w-sm w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-white">Kod QR zlecenia</h3>
              <button
                onClick={() => setShowQRModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="bg-white p-4 rounded-lg mb-4">
              <QRCodeSVG
                value={generateQRCode(selectedOrder)}
                size={256}
                level="H"
                includeMargin={true}
              />
            </div>
            
            <p className="text-center text-gray-400 text-sm mb-4">
              Zlecenie #{selectedOrder.order_number}
            </p>
            
            <button
              onClick={() => {
                const canvas = document.querySelector('canvas');
                if (canvas) {
                  const url = canvas.toDataURL('image/png');
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `QR_${selectedOrder.order_number}.png`;
                  a.click();
                }
              }}
              className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-all flex items-center justify-center gap-2"
            >
              <Download className="w-4 h-4" />
              Pobierz kod QR
            </button>
          </div>
        </div>
      )}

      {/* Modal nowego zlecenia */}
      <NewOrderModal
        isOpen={showNewOrderModal}
        onClose={() => setShowNewOrderModal(false)}
        onSuccess={fetchOrders}
      />
    </div>
  );
};